import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Mail, Phone, Clock, Send, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { contactAPI } from '../services/api';

const Contact = ({ cartCount }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await contactAPI.send(formData);
      toast({
        title: 'Message sent! ✉️',
        description: 'Thank you for contacting us. We\'ll get back to you soon.',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try WhatsApp or email directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <SEO 
        title="Contact Us"
        description="Get in touch with Adina's Crafts. Questions about our handmade candles? Contact us via email, phone or WhatsApp. We'd love to hear from you!"
        keywords="contact Adina's Crafts, candle shop contact, UK candle maker contact"
        url="/contact"
      />
      <Navbar cartCount={cartCount} />

      {/* Hero */}
      <section className="bg-black py-20 text-white text-center border-b border-gray-800">
        <div className="container mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-serif font-bold mb-4 text-amber-400">Contact Us</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            We'd love to hear from you. Get in touch with us today!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-serif font-bold text-amber-400 mb-6">
                Send us a message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    required
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    required
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Subject
                  </label>
                  <Input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    placeholder="How can we help?"
                    required
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Tell us more about your enquiry..."
                    rows={5}
                    required
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-400 hover:bg-amber-500 text-black font-semibold py-6 text-lg disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-serif font-bold text-amber-400 mb-6">
                  Get in Touch
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Email</h4>
                      <a 
                        href="mailto:contact@adinascrafts.co.uk" 
                        className="text-amber-400 hover:text-amber-300 transition-colors"
                      >
                        contact@adinascrafts.co.uk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Phone</h4>
                      <a 
                        href="tel:+447818983777" 
                        className="text-amber-400 hover:text-amber-300 transition-colors"
                      >
                        07818 983 777
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">WhatsApp</h4>
                      <a 
                        href="https://wa.me/447818983777?text=Hi%20Adina's%20Crafts!%20%F0%9F%91%8B"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#25D366] hover:text-green-400 transition-colors"
                      >
                        Chat with us on WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl shadow-lg p-8 text-black">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-8 h-8" />
                  <h3 className="text-2xl font-serif font-bold">
                    Business Hours
                  </h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-black/10 rounded-lg p-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="font-bold text-lg">We're Always Open!</span>
                  </div>
                  <p className="text-black/80">
                    Feel free to contact us anytime via WhatsApp, email, or phone. We'll respond as quickly as possible.
                  </p>
                  <div className="pt-2 border-t border-black/20">
                    <p className="text-sm text-black/70">
                      💬 Quickest response via WhatsApp
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Contact Card */}
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                <h4 className="font-semibold text-white mb-4">Quick Contact</h4>
                <div className="flex gap-3">
                  <a
                    href="https://wa.me/447818983777?text=Hi%20Adina's%20Crafts!%20%F0%9F%91%8B"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-lg py-3 px-4 text-center font-semibold transition-colors"
                  >
                    WhatsApp
                  </a>
                  <a
                    href="mailto:contact@adinascrafts.co.uk"
                    className="flex-1 bg-amber-400 hover:bg-amber-500 text-black rounded-lg py-3 px-4 text-center font-semibold transition-colors"
                  >
                    Email
                  </a>
                  <a
                    href="tel:+447818983777"
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white rounded-lg py-3 px-4 text-center font-semibold transition-colors"
                  >
                    Call
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
